/**
 * ChamadaRoom — tela unificada de videochamada
 * Usado por triagem e atendimento via prop `mode`
 *
 * Rotas:
 *   <ChamadaRoom mode="triagem" />      → /app/home/:urlRoom
 *   <ChamadaRoom mode="atendimento" />  → /app/home/atendimento/:urlRoom
 */
import { CircularProgress, Tab, Tabs } from "@mui/material";
import Video from "twilio-video";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ButtonComponent from "../../components/ButtonComponent";
import formatName from "../../helpers/formatName";
import {
  EntarTriagemAction,
  EntrarAtendimentoAction,
  FinalizarChamadaAtendimento,
  ListarAtendentesOnlineAction,
  ListarAtendimentoPorSalaAction,
  TransferirChamadaAction,
} from "../../store/actions/triagem";
import PartesEnvolvidas from "./partesEnvolvidas";
import PartesEnvolvidasAtendimento from "./partesEnvolvidasAtendimento";
import CategoriaAtendimentoSelecionada from "./categoriaAtendimentoSelecionada";
import ModalEncaminhamentoChamada from "./modalAtendenteEncaminhamento";
import io from "socket.io-client";
import { urlApi } from "../../services/url";
import { useTwilioRoom } from "../../hooks/useTwilioRoom";

const MicIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 1a4 4 0 0 1 4 4v6a4 4 0 0 1-8 0V5a4 4 0 0 1 4-4zm6.5 9a.5.5 0 0 1 .5.5A7 7 0 0 1 12.5 17v2.5H15a.5.5 0 0 1 0 1H9a.5.5 0 0 1 0-1h2.5V17A7 7 0 0 1 5 10.5a.5.5 0 0 1 1 0 6 6 0 0 0 12 0 .5.5 0 0 1 .5-.5z"/></svg>;
const MicOffIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M3.27 3L2 4.27l6.01 6.01V11a4 4 0 0 0 7.08 2.55l1.49 1.49A5.97 5.97 0 0 1 13 17v2.5H15a.5.5 0 0 1 0 1H9a.5.5 0 0 1 0-1h2.5V17a7 7 0 0 1-6.95-6.18L3.27 3zM12 1a4 4 0 0 1 4 4v4.18l-1.5-1.5A2.5 2.5 0 0 0 12 2.5 2.5 2.5 0 0 0 9.5 5v1.68L8 5.18V5a4 4 0 0 1 4-4z"/></svg>;
const CamIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4z"/></svg>;
const CamOffIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M21 6.5l-4 4V7a1 1 0 0 0-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12c.21 0 .39-.08.55-.18L19.73 21 21 19.73 3.27 2z"/></svg>;
const PhoneIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 0 0-1 .23l-2.2 2.2a15.045 15.045 0 0 1-6.52-6.51l2.2-2.21a.98.98 0 0 0 .23-1A11.4 11.4 0 0 1 8.62 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.62c0-.55-.45-1-1-1z"/></svg>;

function VideoTrack({ track }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !track) return;
    track.attach(ref.current);
    return () => track.detach(ref.current);
  }, [track]);
  return <video ref={ref} autoPlay playsInline style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />;
}

function RemoteVideo({ participant }) {
  const [videoTrack, setVideoTrack] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!participant) return;

    function attachAudio(track) {
      if (audioRef.current) track.attach(audioRef.current);
    }
    function detachAudio(track) {
      track.detach().forEach(el => el.remove());
      if (audioRef.current) track.detach(audioRef.current);
    }
    function onTrackSubscribed(track) {
      if (track.kind === "video") setVideoTrack(track);
      if (track.kind === "audio") attachAudio(track);
    }
    function onTrackUnsubscribed(track) {
      if (track.kind === "video") setVideoTrack(null);
      if (track.kind === "audio") detachAudio(track);
    }

    participant.tracks.forEach(pub => {
      if (pub.isSubscribed && pub.track) onTrackSubscribed(pub.track);
    });

    function onTrackPublished(pub) {
      if (pub.isSubscribed && pub.track) { onTrackSubscribed(pub.track); return; }
      pub.on("subscribed", onTrackSubscribed);
      pub.on("unsubscribed", onTrackUnsubscribed);
    }

    participant.on("trackSubscribed", onTrackSubscribed);
    participant.on("trackUnsubscribed", onTrackUnsubscribed);
    participant.on("trackPublished", onTrackPublished);

    return () => {
      participant.off("trackSubscribed", onTrackSubscribed);
      participant.off("trackUnsubscribed", onTrackUnsubscribed);
      participant.off("trackPublished", onTrackPublished);
    };
  }, [participant]);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <audio ref={audioRef} autoPlay style={{ display: "none" }} />
      {videoTrack ? <VideoTrack track={videoTrack} /> : (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 72, height: 72, borderRadius: "50%", background: "#374151", color: "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 700 }}>
            {participant.identity?.charAt(0)?.toUpperCase() ?? "?"}
          </div>
        </div>
      )}
    </div>
  );
}

function DraggablePiP({ participant, isCameraOn, containerRef }) {
  const [track, setTrack] = useState(null);
  const [pos, setPos] = useState({ x: 10, y: 48 });
  const [hovered, setHovered] = useState(false);
  const dragging = useRef(false);
  const startMouse = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!participant) return;
    participant.videoTracks.forEach((pub) => pub.track && setTrack(pub.track));
  }, [participant]);

  function onMouseDown(e) {
    e.preventDefault();
    dragging.current = true;
    startMouse.current = { x: e.clientX, y: e.clientY };
    startPos.current = { ...pos };
  }

  useEffect(() => {
    function onMouseMove(e) {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const dx = e.clientX - startMouse.current.x;
      const dy = e.clientY - startMouse.current.y;
      const newX = Math.max(8, Math.min(rect.width - 108, startPos.current.x - dx));
      const newY = Math.max(48, Math.min(rect.height - 78, startPos.current.y - dy));
      setPos({ x: newX, y: newY });
    }
    function onMouseUp() { dragging.current = false; }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const W = hovered ? 180 : 96;
  const H = hovered ? 126 : 68;

  return (
    <div
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "absolute", right: pos.x, bottom: pos.y,
        width: W, height: H, borderRadius: 8, overflow: "hidden",
        border: "2px solid rgba(255,255,255,0.2)", background: "#1f2937",
        boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
        cursor: dragging.current ? "grabbing" : "grab",
        transition: "width 0.2s ease, height 0.2s ease",
        zIndex: 10, userSelect: "none",
      }}
    >
      {isCameraOn && track ? <VideoTrack track={track} /> : (
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: "#6b7280", fontSize: 9, fontFamily: "Poppins" }}>Câmera off</span>
        </div>
      )}
      {hovered && (
        <div style={{ position: "absolute", bottom: 4, left: 0, right: 0, textAlign: "center", fontSize: 9, color: "rgba(255,255,255,0.5)", fontFamily: "Poppins", pointerEvents: "none" }}>
          arraste para mover
        </div>
      )}
    </div>
  );
}

function CtrlBtn({ onClick, active = true, danger, title, children }) {
  return (
    <button onClick={onClick} title={title} style={{
      background: danger ? "#ef4444" : active ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.07)",
      border: danger ? "none" : "1px solid rgba(255,255,255,0.15)",
      color: "#fff", borderRadius: 50, width: 38, height: 38,
      display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer", transition: "opacity 0.15s",
    }}
      onMouseEnter={e => e.currentTarget.style.opacity = "0.75"}
      onMouseLeave={e => e.currentTarget.style.opacity = "1"}
    >
      {children}
    </button>
  );
}

function ActionBtn({ onClick, loading, color, label, loadingLabel }) {
  const base = { border: "none", borderRadius: 7, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Poppins, sans-serif", transition: "opacity 0.15s" };
  return loading ? (
    <div style={{ ...base, background: color, color: "#fff", display: "flex", alignItems: "center", gap: 6 }}>
      <CircularProgress style={{ color: "#fff", width: 13, height: 13 }} />
      {loadingLabel ?? "Aguarde"}
    </div>
  ) : (
    <button onClick={onClick} style={{ ...base, background: color, color: "#fff" }}
      onMouseEnter={e => e.currentTarget.style.opacity = "0.8"}
      onMouseLeave={e => e.currentTarget.style.opacity = "1"}
    >
      {label}
    </button>
  );
}

function DescricaoPanel({ titulo, texto, autor, updatedAt, readOnly = false, onEdit, onSave, onCancel, editando, value, onChange }) {
  if (readOnly) return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#374151" }}>{titulo}</p>
      {autor && <div style={{ fontSize: 11, color: "#9ca3af" }}>{autor}{updatedAt && ` · ${dayjs(updatedAt).format("DD/MM/YYYY HH:mm")}`}</div>}
      <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, background: "#f9fafb", borderRadius: 8, padding: 12, minHeight: 80 }}>
        {texto || <span style={{ color: "#d1d5db" }}>Sem descrição.</span>}
      </div>
    </div>
  );

  return editando ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#374151" }}>{titulo}</p>
      <textarea value={value} onChange={onChange} style={{ flex: 1, minHeight: 160, borderRadius: 8, border: "1px solid #e5e7eb", padding: 10, fontSize: 13, fontFamily: "Poppins, sans-serif", resize: "none", outline: "none" }} />
      <ButtonComponent onClick={onSave} fontWeight="500" fullWidth>Salvar</ButtonComponent>
      <button onClick={onCancel} style={{ background: "none", border: "none", color: "#9ca3af", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
    </div>
  ) : (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#374151" }}>{titulo}</p>
      {autor && updatedAt && <div style={{ fontSize: 11, color: "#9ca3af" }}>{autor} · {dayjs(updatedAt).format("DD/MM/YYYY HH:mm")}</div>}
      {texto?.length > 0 ? (
        <>
          <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6, background: "#f9fafb", borderRadius: 8, padding: 12, minHeight: 80, overflowY: "auto" }}>
            {texto}
          </div>
          <ButtonComponent onClick={onEdit} fontWeight="500" fullWidth>Editar descrição</ButtonComponent>
        </>
      ) : (
        <div style={{ textAlign: "center", paddingTop: 24 }}>
          <p style={{ color: "#9ca3af", fontSize: 13 }}>Nenhuma descrição ainda.</p>
          <ButtonComponent onClick={onEdit} fontWeight="500" fullWidth>Adicionar descrição</ButtonComponent>
        </div>
      )}
    </div>
  );
}

function ChamadaRoom({ mode }) {
  const isTriagem = mode === "triagem";

  const { room, remoteParticipants, connecting, isCameraOn, isMicOn, connect, disconnect, toggleCamera, toggleMic } = useTwilioRoom();
  const navigate = useNavigate();
  const { urlRoom } = useParams();
  const { atendentesOnline } = useSelector((state) => state.triagem);

  const [dataRoom, setDataRoom] = useState({});
  const [descricao, setDescricao] = useState("");
  const [editandoDescricao, setEditandoDescricao] = useState(false);
  const [novoUsuario, setNovoUsuario] = useState({ nome: "", documento: "", tipo: "" });
  const [usuarios, setUsuarios] = useState([]);
  const [categoriaSecundaria, setCategoriaSecundaria] = useState("");
  const [atendenteSelected, setAtendenteSelected] = useState(false);
  const [atendenteSelectedData, setAtendenteSelectedData] = useState({});
  const [modalAtendente, setModalAtendente] = useState(false);
  const [loadConfirmSelect, setLoadConfirmSelect] = useState(false);
  const [loadingEncaminhar, setLoadingEncaminhar] = useState(false);
  const [tab, setTab] = useState(0);
  const videoContainerRef = useRef(null);
  const [videoHovered, setVideoHovered] = useState(false);

  const tabs = isTriagem
    ? ["Partes Envolvidas", "Descrição"]
    : ["Partes Envolvidas", "Desc. Triagem", "Minha Descrição"];

  useEffect(() => {
    async function init() {
      const res = await ListarAtendimentoPorSalaAction(urlRoom);
      setDataRoom(res);
      setUsuarios(res.partes_envolvidas ?? []);
      setDescricao(isTriagem ? (res.triagem_descricao ?? "") : (res.atendente_descricao ?? ""));
      if (res.token) await connect(res.token, res.sala);
    }
    init();
  }, []);

  useEffect(() => {
    const socket = io(urlApi);
    socket.on("atendimento-finalizado", async (data) => {
      const res = await ListarAtendimentoPorSalaAction(urlRoom);
      if (data.sala === res.sala) { disconnect(); navigate("/app/home"); }
    });
    return () => socket.disconnect();
  }, []);

  async function salvarDescricao() {
    try {
      if (isTriagem) {
        await EntarTriagemAction({ sala: urlRoom, triagem_descricao: descricao, categoria_secundaria: categoriaSecundaria, partes_envolvidas: JSON.stringify(usuarios) });
      } else {
        await EntrarAtendimentoAction({ sala: urlRoom, atendente_descricao: descricao });
      }
      const res = await ListarAtendimentoPorSalaAction(urlRoom);
      setDataRoom(res);
      setEditandoDescricao(false);
    } catch (e) { console.log(e); }
  }

  async function finalizarChamada() {
    try {
      await FinalizarChamadaAtendimento({ sala: dataRoom.sala });
      disconnect();
      navigate("/app/home");
    } catch (e) { console.log(e); }
  }

  async function listarAtendentesOnline() {
    setLoadingEncaminhar(true);
    try {
      await EntarTriagemAction({ sala: urlRoom, triagem_descricao: descricao, categoria_secundaria: categoriaSecundaria, partes_envolvidas: JSON.stringify(usuarios) });
      await ListarAtendentesOnlineAction(dataRoom.modalidade_id);
      setModalAtendente(true);
    } catch (e) { console.log(e); }
    finally { setLoadingEncaminhar(false); }
  }

  async function transferirChamada() {
    setLoadConfirmSelect(true);
    try {
      await TransferirChamadaAction({ sala: dataRoom.sala, atendente_id: atendenteSelectedData.usuario_id });
      disconnect();
      navigate("/app/home");
    } catch (e) { setLoadConfirmSelect(false); }
  }

  function toggleFullscreen() {
    if (!videoContainerRef.current) return;
    if (!document.fullscreenElement) {
      videoContainerRef.current.requestFullscreen().catch(console.error);
    } else {
      document.exitFullscreen();
    }
  }

  const hasRemote = remoteParticipants.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, height: "100%", paddingBottom: 8, fontFamily: "Poppins, sans-serif" }}>

      {/* ── Barra superior ────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", borderRadius: 10, padding: "8px 16px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#eff6ff", color: "#3b82f6", fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {dataRoom?.nome?.charAt(0)?.toUpperCase() ?? "?"}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{formatName(dataRoom.nome)}</div>
            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
              {formatName(dataRoom.modalidade)}
              {isTriagem && dataRoom.parceiro_integracao_nome && ` · ${formatName(dataRoom.parceiro_integracao_nome)}`}
              {!isTriagem && dataRoom.categoria_principal_descricao && (
                <> · {formatName(dataRoom.categoria_principal_descricao)} / {formatName(dataRoom.categoria_secundaria_descricao)}</>
              )}
            </div>
          </div>
        </div>

        {isTriagem && (
          <div style={{ flex: 1, minWidth: 260, maxWidth: 460 }}>
            <CategoriaAtendimentoSelecionada
              modalidadeId={dataRoom.modalidade_id}
              categoriaSecundariaSelecionada={categoriaSecundaria}
              setCategoriaSecundariaSelecionada={setCategoriaSecundaria}
              data={dataRoom}
            />
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          {isTriagem && (
            <ActionBtn onClick={listarAtendentesOnline} loading={loadingEncaminhar} color="#0ea5e9" label="Encaminhar" loadingLabel="Carregando" />
          )}
          <ActionBtn onClick={finalizarChamada} color="#ef4444" label="Finalizar" />
        </div>
      </div>

      {/* ── Corpo ─────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 8, flex: 1, minHeight: 0 }}>

        {/* Coluna esquerda: vídeo + alertas abaixo */}
        <div style={{ flex: "0 0 55%", display: "flex", flexDirection: "column", gap: 6, minHeight: 0 }}>

          {/* Box do vídeo */}
          <div
            ref={videoContainerRef}
            onMouseEnter={() => setVideoHovered(true)}
            onMouseLeave={() => setVideoHovered(false)}
            style={{ flex: 1, background: "#0f172a", borderRadius: 12, position: "relative", overflow: "hidden", minHeight: 320, maxHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
          {connecting && (
            <div style={{ color: "#6b7280", fontSize: 13, display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
              <CircularProgress style={{ color: "#6b7280", width: 24, height: 24 }} />
              Conectando à sala...
            </div>
          )}
          {!connecting && !hasRemote && (
            <div style={{ color: "#4b5563", fontSize: 13, textAlign: "center", padding: "0 24px" }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
              <div style={{ color: "#6b7280", fontWeight: 600, marginBottom: 6 }}>Aguardando paciente...</div>
              <div style={{ fontSize: 11, color: "#4b5563", lineHeight: 1.6 }}>
                Vídeo não apareceu? Peça ao paciente que clique em{" "}
                <span style={{ color: "#94a3b8", fontWeight: 600 }}>"Recarregar"</span>{" "}
                na tela dele.
              </div>
            </div>
          )}
          {hasRemote && (
            <div style={{ position: "absolute", inset: 0 }}>
              <RemoteVideo participant={remoteParticipants[0]} />
            </div>
          )}

          {/* Badge AO VIVO — canto superior esquerdo */}
          {room && (
            <div style={{
              position: "absolute", top: 10, left: 10, zIndex: 10,
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(0,0,0,0.55)", borderRadius: 20,
              padding: "3px 10px",
              border: "1px solid rgba(34,197,94,0.35)",
            }}>
              <div style={{
                width: 6, height: 6, borderRadius: "50%", background: "#22c55e",
                animation: "livepulse 2s infinite",
              }} />
              <span style={{ fontSize: 10, fontWeight: 700, color: "#e2e8f0", fontFamily: "Poppins, sans-serif", letterSpacing: "0.05em" }}>
                AO VIVO
              </span>
              <style>{`@keyframes livepulse{0%,100%{opacity:1}50%{opacity:0.4}}`}</style>
            </div>
          )}

          {/* Fullscreen */}
          <button
            onClick={toggleFullscreen}
            title="Tela cheia"
            style={{
              position: "absolute", top: 10, right: 10,
              background: "rgba(0,0,0,0.5)", border: "none",
              color: "#fff", borderRadius: 6, padding: "5px 8px",
              cursor: "pointer", fontSize: 14, lineHeight: 1,
              opacity: videoHovered ? 1 : 0,
              transition: "opacity 0.2s",
              zIndex: 10,
            }}
          >
            ⛶
          </button>

          {/* PiP local arrastável */}
          {room && (
            <DraggablePiP
              participant={room.localParticipant}
              isCameraOn={isCameraOn}
              containerRef={videoContainerRef}
            />
          )}

          {/* Controles */}
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 10, padding: "8px 0 10px", background: "linear-gradient(transparent, rgba(0,0,0,0.6))" }}>
            <CtrlBtn onClick={toggleMic} active={isMicOn} title={isMicOn ? "Silenciar" : "Ativar mic"}>
              {isMicOn ? <MicIcon /> : <MicOffIcon />}
            </CtrlBtn>
            <CtrlBtn onClick={toggleCamera} active={isCameraOn} title={isCameraOn ? "Desligar câmera" : "Ligar câmera"}>
              {isCameraOn ? <CamIcon /> : <CamOffIcon />}
            </CtrlBtn>
            <CtrlBtn onClick={finalizarChamada} danger title="Encerrar">
              <PhoneIcon />
            </CtrlBtn>
          </div>

          </div>

          {/* Alertas abaixo do box do vídeo — fora do overflow:hidden */}
          {room && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "7px 12px" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#f97316", flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: "#9a3412", fontFamily: "Poppins, sans-serif" }}>
                  Vídeo do paciente não apareceu? Pressione <b>F5</b> para recarregar sua página.
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "7px 12px" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#f97316", flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: "#9a3412", fontFamily: "Poppins, sans-serif" }}>
                  Ou peça ao paciente que clique em <b>Recarregar</b> no aplicativo dele.
                </span>
              </div>
            </div>
          )}

        </div>{/* fim coluna esquerda */}

        {/* Painel lateral */}
        <div style={{ flex: 1, background: "#fff", borderRadius: 12, boxShadow: "0 1px 6px rgba(0,0,0,0.07)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="fullWidth"
            sx={{
              borderBottom: "1px solid #f0f0f0",
              "& .MuiTab-root": { fontFamily: "Poppins", fontSize: 11, fontWeight: 600, textTransform: "none" },
              "& .Mui-selected": { color: "#111827" },
              "& .MuiTabs-indicator": { background: "#111827" },
            }}
          >
            {tabs.map((t) => <Tab key={t} label={t} />)}
          </Tabs>

          <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
            {tab === 0 && (
              isTriagem ? (
                <PartesEnvolvidas novoUsuario={novoUsuario} setNovoUsuario={setNovoUsuario} usuarios={usuarios} setUsuarios={setUsuarios} />
              ) : (
                <PartesEnvolvidasAtendimento usuarios={dataRoom.partes_envolvidas} />
              )
            )}
            {tab === 1 && (
              isTriagem ? (
                <DescricaoPanel
                  titulo="Descrição da triagem"
                  texto={dataRoom.triagem_descricao}
                  autor={formatName(dataRoom.nomeAtendenteTriagem)}
                  updatedAt={dataRoom.updated_at}
                  editando={editandoDescricao}
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  onEdit={() => setEditandoDescricao(true)}
                  onSave={salvarDescricao}
                  onCancel={() => setEditandoDescricao(false)}
                />
              ) : (
                <DescricaoPanel
                  titulo="Descrição da triagem"
                  texto={dataRoom.triagem_descricao}
                  autor={`Triagem: ${formatName(dataRoom.nomeAtendenteTriagem)}`}
                  readOnly
                />
              )
            )}
            {tab === 2 && !isTriagem && (
              <DescricaoPanel
                titulo="Minha descrição"
                texto={dataRoom.atendente_descricao}
                autor={formatName(dataRoom.nomeAtendente)}
                updatedAt={dataRoom.updated_at}
                editando={editandoDescricao}
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                onEdit={() => setEditandoDescricao(true)}
                onSave={salvarDescricao}
                onCancel={() => setEditandoDescricao(false)}
              />
            )}
          </div>
        </div>
      </div>


      {isTriagem && (
        <ModalEncaminhamentoChamada
          setModalAtendente={setModalAtendente}
          modalAtendente={modalAtendente}
          atendentesOnline={atendentesOnline}
          atendenteSelected={atendenteSelected}
          SelecionarAtendente={(at) => { setAtendenteSelectedData(at); setAtendenteSelected(true); }}
          atendenteSelectedData={atendenteSelectedData}
          TransferirChamada={transferirChamada}
          EscolherOutro={() => { setAtendenteSelectedData({}); setAtendenteSelected(false); }}
          loading={loadConfirmSelect}
        />
      )}
    </div>
  );
}

export default ChamadaRoom;